

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useTable, useGlobalFilter } from 'react-table';
import { Link } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import { Pencil, Plus, Download, Search, Trash2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './farmdata.css';

const DataTableEmp = () => {
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [newEmp, setNewEmp] = useState({
    empid: '',
    empname: '',
    ph: '',
    password: '',
    role: 'employee',
  });

  const [editEmp, setEditEmp] = useState({
    empid: '',
    empname: '',
    ph: '',
    password: ''
  });

  const tableRef = useRef();

  useEffect(() => {
    fetch('https://kmtea.onrender.com/employee')
      .then(res => res.json())
      .then(setData)
      .catch(console.error);
  }, []);

  const handleDeleteEmployee = async () => {
    const res = await fetch(`https://kmtea.onrender.com/employee/${selectedEmpId}`, {
      method: 'DELETE',
    });
    const result = await res.json();

    if (result.success) {
      setData(prev => prev.filter(emp => emp.empid !== selectedEmpId));
      toast.success('Employee deleted successfully');
    } else {
      toast.error('Failed to delete employee');
    }

    setShowDeleteModal(false);
    setSelectedEmpId(null);
  };

  const columns = useMemo(() => [
    { Header: 'Emp ID', accessor: 'empid' },
    {
      Header: 'Name',
      accessor: 'empname',
      Cell: ({ row }) => (
        <Link
          to={`/dashboard/employee/${row.original.empid}`}
          state={row.original}
          className="farmer-link"
        >
          {row.original.empname}
        </Link>
      )
    },
    { Header: 'Phone Number', accessor: 'ph' },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableSortBy: true,
      Cell: ({ row }) => {
        const role = row.original.role;
        let iconColor = '#555';
        if (role === 'admin') iconColor = '#e91e63';
        else if (role === 'employee') iconColor = '#3f51b5';
        else iconColor = '#009688';

        return (
          <div className="action-icons" style={{ gap: '12px' }}>
            <button
              onClick={() => {
                setEditEmp(row.original);
                setShowEditPassword(false);
                setIsEditOpen(true);
              }}
              title="Edit"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <Pencil size={18} color={iconColor} />
            </button>
            <button
              onClick={() => {
                setSelectedEmpId(row.original.empid);
                setShowDeleteModal(true);
              }}
              title="Delete"
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <Trash2 size={18} color="red" />
            </button>
          </div>
        );
      }
    }
  ], []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    setGlobalFilter: setFilter
  } = useTable({ columns, data }, useGlobalFilter);

  const handleSearch = (e) => {
    const value = e.target.value || undefined;
    setGlobalFilter(value);
    setFilter(value);
  };

  const handleDownloadPDF = () => {
    html2pdf().from(tableRef.current).save('employees.pdf');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewEmp(prev => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditEmp(prev => ({ ...prev, [name]: value }));
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    const res = await fetch('https://kmtea.onrender.com/employeeadd', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newEmp)
    });
    const result = await res.json();
    if (result.success) {
      setData(prev => [...prev, result.newEmployee]);
      setNewEmp({ empid: '', empname: '', ph: '', password: '', role: 'employee' });
      setShowAddPassword(false);
      setIsModalOpen(false);
      toast.success('Employee added successfully');
    } else {
      toast.error('Failed to add');
    }
  };

  const handleUpdateEmployee = async (e) => {
    e.preventDefault();
    const res = await fetch(`https://kmtea.onrender.com/employee/${editEmp.empid}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editEmp)
    });
    const result = await res.json();
    if (result.success) {
      const updated = data.map(emp =>
        emp.empid === editEmp.empid ? { ...emp, ...editEmp } : emp
      );
      setData(updated);
      setIsEditOpen(false);
      toast.success('Employee updated successfully');
    } else {
      toast.error('Update failed');
    }
  };

  return (
    <div className="datatable-container">
      <div className="header-actions">
        <h2>Employee Details</h2>
        <div className="search-filter-wrapper">
          <Search size={16} />
          <input
            type="text"
            placeholder="Quick search..."
            value={globalFilter || ''}
            onChange={handleSearch}
          />
        </div>
        <div className="action-btns">
          <button onClick={handleDownloadPDF}>
            <Download size={16} /> Download
          </button>
          <button onClick={() => {
            setIsModalOpen(true);
            setShowAddPassword(false);
          }}>
            <Plus size={16} /> Add New
          </button>
        </div>
      </div>

      <div ref={tableRef}>
        <table {...getTableProps()} className="styled-table">
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} key={row.id}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2>+ Add New Employee</h2>
            <form onSubmit={handleAddEmployee}>
              <div className="form-group">
                <label>Name</label>
                <input type="text" name="empname" value={newEmp.empname} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="text" name="ph" value={newEmp.ph} onChange={handleInputChange} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showAddPassword ? 'text' : 'password'}
                    name="password"
                    value={newEmp.password}
                    onChange={handleInputChange}
                  />
                  <span
                    onClick={() => setShowAddPassword(prev => !prev)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer'
                    }}
                  >
                    {showAddPassword ? '🙈' : '👁️'}
                  </span>
                </div>
              </div>
              <button type="submit">Add</button>
              <button type="button" onClick={() => setIsModalOpen(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2>Edit Employee</h2>
            <form onSubmit={handleUpdateEmployee}>
              <div className="form-group">
                <label>Emp ID</label>
                <input type="text" name="empid" value={editEmp.empid} disabled />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input type="text" name="empname" value={editEmp.empname} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input type="text" name="ph" value={editEmp.ph} onChange={handleEditChange} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showEditPassword ? 'text' : 'password'}
                    name="password"
                    value={editEmp.password}
                    onChange={handleEditChange}
                  />
                  <span
                    onClick={() => setShowEditPassword(prev => !prev)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer'
                    }}
                  >
                    {showEditPassword ? '🙈' : '👁️'}
                  </span>
                </div>
              </div>
              <button type="submit">Update</button>
              <button type="button" onClick={() => setIsEditOpen(false)}>Cancel</button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this employee?</p>
            <button onClick={handleDeleteEmployee}>Yes, Delete</button>
            <button onClick={() => setShowDeleteModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default DataTableEmp;
